import { useQuery } from '@tanstack/react-query';
import { Alert, t } from '@kanbios/ui';
import { api, unwrap } from '../../lib/api';

const ACTIVE = new Set(['queued', 'running']);
/** D43-2: the conditions that say Checkmk reads the agent and its checks. */
const OBSERVED = ['AgentReachable', 'ChecksReceived'] as const;
const DAY = 86_400_000;

/**
 * D68: at the top of the Health tab, why the agent's checks are « À traiter »
 * while the server is being enrolled: running, failed, or done and not read by
 * Checkmk yet. Nothing once Checkmk reads the agent and its checks.
 */
export function EnrolmentHealthNotice({ server, jobs, canEnrol, onRetry }: {
  server: string;
  /** the server's enrolments, latest first (configuration endpoint) */
  jobs: { id: string; status: string; finishedAt: string | null }[] | undefined;
  canEnrol: boolean;
  onRetry: () => void;
}) {
  const latest = jobs?.[0];
  // A recent success: until Checkmk reads the agent and its first checks (read every 10 s, as the header's band).
  const recent = latest?.status === 'succeeded' && latest.finishedAt !== null && Date.now() - new Date(latest.finishedAt).getTime() < DAY;
  const observed = useQuery({
    queryKey: ['server-synchronisation', server],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}/synchronisation', { params: { path: { server } } })),
    enabled: recent,
    retry: false,
    refetchInterval: query => (OBSERVED.every(type => query.state.data?.conditions.find(c => c.type === type)?.status === 'True') ? false : 10_000),
  });
  if (!latest) return null;
  if (ACTIVE.has(latest.status)) return <Alert tone="info" title={t('enrolmentNotice.running.title')}>{t('enrolmentNotice.running.body')}</Alert>;
  if (latest.status === 'failed') {
    return (
      <Alert tone="neg" title={t('enrolmentNotice.failed.title')} action={canEnrol ? t('enrolmentNotice.failed.retry') : undefined} onAction={canEnrol ? onRetry : undefined}>
        {t('enrolmentNotice.failed.body')}
      </Alert>
    );
  }
  const waiting = recent && observed.data && OBSERVED.some(type => observed.data.conditions.find(c => c.type === type)?.status !== 'True');
  return waiting ? <Alert tone="info" title={t('enrolmentNotice.awaiting.title')}>{t('enrolmentNotice.awaiting.body')}</Alert> : null;
}
